<template>
	<div class="user-menu-container">
		<v-menu offset-y>
			<template #activator="{ on, attrs }">
				<v-btn
					class="text-body-2 text-none px-1 user-menu-btn"
					text
					v-bind="attrs"
					v-on="on"
				>
					<v-avatar
						size="32"
						class="mr-2 user-menu-avatar"
					>
						<v-icon
							size="20"
							class="user-menu-icon"
						>
							mdi-account-outline
						</v-icon>
					</v-avatar>
					<span>
						{{ displayName }}
					</span>
					<v-icon
						size="18"
						class="syrenis-black--text ml-1"
					>
						mdi-chevron-down
					</v-icon>
				</v-btn>
			</template>
			<v-list>
				<!-- Preferences - only show for Admin Portal users -->
				<v-list-item
					v-if="showPreferences"
					@click="openPreferences"
				>
					<v-icon
						class="mr-3"
						small
					>
						mdi-cog-outline
					</v-icon>
					Preferences
				</v-list-item>
				<template v-if="externalLinks && externalLinks.length > 0">
					<v-divider v-if="showPreferences" />
					<v-list-item
						v-for="link in externalLinks"
						:key="link.text"
						@click="openExternalLink(link)"
					>
						<v-icon
							class="mr-3"
							small
						>
							{{ getExternalLinkIcon(link) }}
						</v-icon>
						{{ getExternalLinkText(link) }}
					</v-list-item>
				</template>
				<v-divider />
				<v-list-item @click="handleLogout">
					<v-icon
						class="mr-3"
						small
					>
						mdi-logout
					</v-icon>
					Log Out
				</v-list-item>
			</v-list>
		</v-menu>
		<PreferencesSheet
			v-model="preferencesOpen"
			@theme-change="handleThemeChange"
			@colors-updated="handleColorsUpdated"
		/>
	</div>
</template>

<script>
import PreferencesSheet from './preferences-sheet.vue'
import { externalLinks } from '../state/admin-portal-navigation.js'
import { zohoNewTicketLink, zohoViewTicketLink, learningHubLink } from '../state/config-keys.js'
import { applyDarkMode, loadDarkModePreference } from '../utils/dark-mode.js'

export default {
	name: 'user-menu',

	components: {
		PreferencesSheet
	},

	inject: {
		// Allow parent to inject logout function
		logOutAndRedirect: { default: null }
	},

	props: {
		// User's full name - can be passed as prop or injected
		userFullName: {
			type: [String, Function],
			default: ''
		}
	},

	setup () {
		return {
			externalLinks,
			zohoNewTicketLink,
			zohoViewTicketLink,
			learningHubLink
		}
	},

	data () {
		return {
			preferencesOpen: false
		}
	},

	computed: {
		showPreferences () {
			// Preferences only available to Syrenis Admin users in Admin Portal
			return process.env.VUE_APP_TYPE !== 'CS_PORTAL' && this.displayName === 'Syrenis Admin'
		},

		displayName () {
			// Handle both string prop and function (from inject)
			if (typeof this.userFullName === 'function') {
				return this.userFullName()
			}
			return this.userFullName || ''
		},

		userInitials () {
			const fullName = this.displayName
			if (!fullName) return ''
			const names = fullName.trim().split(/\s+/)
			if (names.length >= 2) {
				return (names[0][0] + names[names.length - 1][0]).toUpperCase()
			}
			return fullName.substring(0, 2).toUpperCase()
		}
	},

	mounted () {
		// Apply saved dark mode preference on mount
		const savedDarkMode = loadDarkModePreference()
		if (savedDarkMode !== null) {
			applyDarkMode(this.$vuetify, savedDarkMode)
		}
	},

	methods: {
		openPreferences () {
			this.preferencesOpen = true
		},

		handleThemeChange (theme) {
			// The PreferencesSheet emits theme changes - we just need to stay in sync
			applyDarkMode(this.$vuetify, theme === 'dark')
		},

		handleColorsUpdated (colors) {
			// Propagate color changes to parent components
			this.$emit('colors-updated', colors)
		},

		handleLogout () {
			// Use injected logout if available, otherwise emit event
			if (this.logOutAndRedirect) {
				this.logOutAndRedirect()
			} else {
				this.$emit('logout')
			}
		},

		openExternalLink (link) {
			const text = (link.text || '').toLowerCase()
			let url = null

			// In Vue 2 with Composition API, refs returned from setup() are auto-unwrapped
			// So this.zohoViewTicketLink IS the URL string, not a ref
			if (text.includes('support') && this.zohoViewTicketLink) {
				url = this.zohoViewTicketLink
			} else if (text.includes('learning') && this.learningHubLink) {
				url = this.learningHubLink
			} else if (link.route) {
				url = link.route
			}

			if (url) {
				window.open(url, '_blank', 'noopener,noreferrer')
			}
		},

		getExternalLinkIcon (link) {
			// Return link's icon if available, otherwise use sensible defaults
			// if (link.icon) return link.icon
			const text = (link.text || '').toLowerCase()
			if (text.includes('support')) return 'mdi-help-circle-outline'
			if (text.includes('learning')) return 'mdi-school-outline'
			return 'mdi-open-in-new'
		},

		getExternalLinkText (link) {
			// Normalize display text for known link types
			const text = (link.text || '').toLowerCase()
			if (text.includes('support')) return 'Support'
			if (text.includes('learning')) return 'Learning Hub'
			return link.text
		}
	}
}
</script>

<style scoped>
.user-menu-container {
	display: inline-flex;
	align-items: center;
}

.user-menu-btn {
	color: var(--global-foreground, #374151);
}

.user-menu-icon {
	color: var(--global-foreground, #374151) !important;
}

.user-menu-avatar {
	background-color: #ebedf0 !important;
}

/* Dark mode styles */
.dark .user-menu-avatar {
	background-color: #3a3a3c !important;
}

.dark .user-menu-icon {
	color: var(--global-foreground, #f5f5f5) !important;
}

::v-deep .v-list-item {
	min-height: 36px !important;
	padding: 0 16px !important;
}

::v-deep .v-list {
	padding: 4px 0 !important;
}
</style>
